import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Keyboard } from 'lucide-react';
import styles from './KeyboardHints.module.css';

interface KeyboardShortcut {
  keys: string[];
  description: string;
}

const shortcuts: KeyboardShortcut[] = [
  { keys: ['1'], description: 'Class tab' },
  { keys: ['2'], description: 'Subclass tab' },
  { keys: ['Esc'], description: 'Collapse all features' }
];

/**
 * KeyboardHints - Shows keyboard shortcuts for power users
 * Desktop only - hidden on mobile devices
 */
const KeyboardHints: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add listener with a small delay to avoid immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.container} ref={containerRef}>
      {/* Toggle Button */}
      <button
        className={styles.toggleButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Show keyboard shortcuts"
        aria-expanded={isOpen}
        title="Keyboard Shortcuts"
      >
        <Keyboard size={18} />
      </button>

      {/* Hints Popover */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
              className={styles.panel}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.15 }}
              role="dialog"
              aria-label="Keyboard shortcuts"
            >
              <div className={styles.header}>
                <h4 className={styles.title}>Keyboard Shortcuts</h4>
                <button
                  className={styles.closeButton}
                  onClick={() => setIsOpen(false)}
                  aria-label="Close keyboard shortcuts"
                >
                  ×
                </button>
              </div>

              <div className={styles.shortcuts}>
                {shortcuts.map((shortcut, index) => (
                  <div key={index} className={styles.shortcut}>
                    <div className={styles.keys}>
                      {shortcut.keys.map((key, keyIndex) => (
                        <kbd key={keyIndex} className={styles.key}>
                          {key}
                        </kbd>
                      ))}
                    </div>
                    <span className={styles.description}>{shortcut.description}</span>
                  </div>
                ))}
              </div>

              <div className={styles.footer}>
                <p className={styles.hint}>
                  Shortcuts are disabled when typing in input fields
                </p>
              </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KeyboardHints;
